import { type FullAirHandlerUnit, type UpdateAirHandlerUnitRequestBody } from "@eisox/api/core-api";
import { useMutation, useQueryClient, type DefaultError, type UseMutationOptions } from "@tanstack/react-query";

import { airHandlerUnitApi, roomApi } from "~/api";

interface UpdateAirHandlerUnitVariables {
  airHandlerUnitId: string;
  body: UpdateAirHandlerUnitRequestBody;
}

/**
 * PATCH one CTA and refresh its queries (`airHandlerUnit`, every `state` variant, and the building
 * list for name/group edits) and the building rooms, whose `airHandlerUnitIds` follow room associations. Group edits call `mutateAsync` once per unit.
 */
export const useUpdateAirHandlerUnit = (
  buildingId: string,
  options?: UseMutationOptions<FullAirHandlerUnit, DefaultError, UpdateAirHandlerUnitVariables>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ airHandlerUnitId, body }: UpdateAirHandlerUnitVariables): Promise<FullAirHandlerUnit> =>
      airHandlerUnitApi.update(buildingId, airHandlerUnitId, body),
    onSuccess: async (data, variables, onMutateResult, context) => {
      const { airHandlerUnitId } = variables;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: airHandlerUnitApi.queries.airHandlerUnit(airHandlerUnitId).queryKey,
        }),
        queryClient.invalidateQueries({ queryKey: [...airHandlerUnitApi.queries.state._def, airHandlerUnitId] }),
        queryClient.invalidateQueries({ queryKey: airHandlerUnitApi.queries.building(buildingId).queryKey }),
        queryClient.invalidateQueries({ queryKey: roomApi.queries.building(buildingId).queryKey }),
      ]);
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
